/**
 * Phase 10 Plan 01 — FOUND-06: Super-admin UI for toggling feature_flags rows.
 * Implements D-01 (admin UI lives inside RolesPermissions, no new top-level route)
 * and D-04 (super_admin-only toggle, RLS-enforced; UI gate is cosmetic).
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

import {
  VentureCard,
  VentureCardHeader,
  VentureCardTitle,
  VentureCardContent,
} from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

type FlagRow = {
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
};

// `feature_flags` is added by migration 20260415000001 but Database types
// haven't been regenerated; cast at the boundary.
type LooseSupabase = {
  from: (t: string) => {
    select: (cols: string) => Promise<{ data: FlagRow[] | null; error: Error | null }>;
    update: (vals: Partial<FlagRow>) => {
      eq: (col: string, val: string) => {
        select: () => Promise<{ data: FlagRow[] | null; error: Error | null }>;
      };
    };
  };
};
const sb = supabase as unknown as LooseSupabase;

function namespaceOf(key: string): "release" | "experiment" | "kill" | "unknown" {
  if (key.startsWith("release.")) return "release";
  if (key.startsWith("experiment.")) return "experiment";
  if (key.startsWith("kill.")) return "kill";
  return "unknown";
}

function namespaceVariant(ns: ReturnType<typeof namespaceOf>) {
  switch (ns) {
    case "release":
      return "default" as const;
    case "experiment":
      return "warning" as const;
    case "kill":
      return "error" as const;
    default:
      return "default" as const;
  }
}

export function FeatureFlagsTab() {
  const queryClient = useQueryClient();
  const flagsQuery = useFeatureFlags();
  const [rows, setRows] = useState<FlagRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  // Detail fetch (description + updated_at + updated_by) — useFeatureFlags only
  // selects key + enabled to keep the bulk hook small. Re-runs whenever the bulk
  // query refreshes (admin-toggle invalidate, 60s tick).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await sb
        .from("feature_flags")
        .select("key, enabled, description, updated_at, updated_by");
      if (cancelled) return;
      if (error) {
        setLoadErr(error.message);
        setRows([]);
        return;
      }
      const sorted = [...(data ?? [])].sort((a, b) => a.key.localeCompare(b.key));
      setRows(sorted);
      setLoadErr(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [flagsQuery.dataUpdatedAt]);

  const handleToggle = async (row: FlagRow, next: boolean) => {
    setSaving((s) => ({ ...s, [row.key]: true }));
    try {
      const { data: userData } = await supabase.auth.getUser();
      const updatedBy = userData?.user?.id ?? null;

      const { data, error } = await sb
        .from("feature_flags")
        .update({ enabled: next, updated_by: updatedBy })
        .eq("key", row.key)
        .select();

      if (error) throw error;

      // Pitfall 2 (RESEARCH): silent RLS 0-row update returns success with empty data.
      if (!data || data.length !== 1) {
        throw new Error(
          "Update returned 0 rows — RLS denied (super_admin role required)."
        );
      }

      // Optimistic local update + invalidate the bulk hook
      setRows((prev) =>
        prev
          ? prev.map((r) => (r.key === row.key ? { ...r, ...data[0] } : r))
          : prev
      );
      await queryClient.invalidateQueries({ queryKey: ["feature_flags"] });
      toast.success(`Flag ${row.key} set to ${next}`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to update ${row.key}: ${msg}`);
      // Revert by triggering a re-fetch
      await queryClient.invalidateQueries({ queryKey: ["feature_flags"] });
    } finally {
      setSaving((s) => {
        const { [row.key]: _omit, ...rest } = s;
        return rest;
      });
    }
  };

  return (
    <VentureCard>
      <VentureCardHeader>
        <VentureCardTitle className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-primary" />
          Feature Flags
        </VentureCardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Toggle gated rollouts, experiments, and kill switches. Changes propagate to all
          clients within 60s.
        </p>
      </VentureCardHeader>
      <VentureCardContent>
        {loadErr ? (
          <div className="py-8 text-center text-red-400">Failed to load flags: {loadErr}</div>
        ) : rows === null ? (
          <div className="py-8 text-center text-muted-foreground">Loading feature flags...</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No feature flags yet — seed one via migration.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Key
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Description
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Namespace
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Updated
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    By
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Enabled
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const ns = namespaceOf(row.key);
                  let when = "—";
                  try {
                    when = formatDistanceToNow(new Date(row.updated_at), {
                      addSuffix: true,
                    });
                  } catch {
                    /* ignore */
                  }
                  return (
                    <tr
                      key={row.key}
                      className="border-b border-border hover:bg-surface transition-colors"
                    >
                      <td className="py-3 px-4 font-mono text-sm">{row.key}</td>
                      <td className="py-3 px-4 text-muted-foreground text-sm">
                        {row.description || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={namespaceVariant(ns)}>{ns}</BadgeVenture>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{when}</td>
                      <td className="py-3 px-4 text-xs text-muted-foreground font-mono">
                        {row.updated_by ? row.updated_by.slice(0, 8) : "system"}
                      </td>
                      <td className="py-3 px-4">
                        <Switch
                          checked={row.enabled}
                          disabled={!!saving[row.key]}
                          onCheckedChange={(checked) => handleToggle(row, checked)}
                          aria-label={`Toggle ${row.key}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </VentureCardContent>
    </VentureCard>
  );
}

export default FeatureFlagsTab;
